// Module Messages - Interface Candidat
import React, { useState } from 'react';
import styled from 'styled-components';
import { theme } from '@theme';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { FloatingChatbot } from '@/components/messages/FloatingChatbot';
import {
  FiMessageCircle, FiSend, FiPaperclip, FiUser,
  FiClock, FiCheckCircle, FiAlertCircle
} from 'react-icons/fi';

// Types
interface Message {
  id: string;
  conversationId: string;
  sender: 'candidate' | 'organization';
  senderName: string;
  content: string;
  timestamp: string;
  read: boolean;
}

interface Conversation {
  id: string;
  subject: string;
  lastMessage: string;
  lastMessageTime: string;
  unreadCount: number;
  status: 'open' | 'closed';
  relatedTo?: string;
}

// Styled Components
const PageHeader = styled.div`
  margin-bottom: ${theme.spacing.xl};
`;

const PageTitle = styled.h1`
  font-size: ${theme.typography.sizes['3xl']};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.sm} 0;
`;

const PageSubtitle = styled.p`
  font-size: ${theme.typography.sizes.base};
  color: ${theme.colors.text}80;
  margin: 0;
`;

const MessagesContainer = styled.div`
  display: grid;
  grid-template-columns: 350px 1fr;
  gap: ${theme.spacing.lg};
  height: calc(100vh - 250px);

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    height: auto;
  }
`;

const ConversationsList = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ConversationsHeader = styled.div`
  padding: ${theme.spacing.lg};
  border-bottom: 1px solid ${theme.colors.surface};
`;

const ConversationsTitle = styled.h3`
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin: 0;
`;

const ConversationsScroll = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const ConversationItem = styled.div<{ active?: boolean; unread?: boolean }>`
  padding: ${theme.spacing.lg};
  border-bottom: 1px solid ${theme.colors.surface};
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  background: ${props => props.active ? theme.colors.primary + '10' : 'transparent'};
  border-left: 3px solid ${props => props.active ? theme.colors.primary : 'transparent'};

  &:hover {
    background: ${theme.colors.surface};
  }

  ${props => props.unread && `
    background: ${theme.colors.info}05;
    font-weight: ${theme.typography.weights.semibold};
  `}
`;

const ConvSubject = styled.div`
  font-size: ${theme.typography.sizes.base};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.xs};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const UnreadBadge = styled.span`
  background: ${theme.colors.primary};
  color: white;
  font-size: ${theme.typography.sizes.xs};
  padding: 2px 8px;
  border-radius: ${theme.borderRadius.full};
  font-weight: ${theme.typography.weights.bold};
`;

const ConvPreview = styled.div`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}60;
  margin-bottom: ${theme.spacing.xs};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ConvTime = styled.div`
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.text}40;
`;

const ChatArea = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ChatHeader = styled.div`
  padding: ${theme.spacing.lg};
  border-bottom: 1px solid ${theme.colors.surface};
  background: ${theme.colors.surface}50;
`;

const ChatTitle = styled.h3`
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.xs} 0;
`;

const ChatSubtitle = styled.div`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}60;
`;

const MessagesArea = styled.div`
  flex: 1;
  padding: ${theme.spacing.xl};
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
  background: ${theme.colors.background};
`;

const MessageBubble = styled.div<{ $isOwn?: boolean }>`
  max-width: 70%;
  align-self: ${props => props.$isOwn ? 'flex-end' : 'flex-start'};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

const MessageHeader = styled.div<{ $isOwn?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.text}60;
  ${props => props.$isOwn && 'flex-direction: row-reverse;'}
`;

const MessageContent = styled.div<{ $isOwn?: boolean }>`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.lg};
  background: ${props => props.$isOwn ? theme.colors.primary : 'white'};
  color: ${props => props.$isOwn ? 'white' : theme.colors.text};
  box-shadow: ${theme.shadows.sm};
  word-wrap: break-word;
`;

const MessageTime = styled.div`
  font-size: ${theme.typography.sizes.xs};
  color: inherit;
  opacity: 0.7;
`;

const InputArea = styled.div`
  padding: ${theme.spacing.lg};
  border-top: 1px solid ${theme.colors.surface};
  background: white;
`;

const InputContainer = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  align-items: flex-end;
`;

const TextInput = styled.textarea`
  flex: 1;
  padding: ${theme.spacing.md};
  border: 1px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.base};
  resize: none;
  min-height: 44px;
  max-height: 120px;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const SendButton = styled.button`
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  background: ${theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.base};
  font-weight: ${theme.typography.weights.semibold};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  transition: all ${theme.transitions.fast};
  white-space: nowrap;

  &:hover {
    background: ${theme.colors.primary}DD;
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AttachButton = styled.button`
  padding: ${theme.spacing.sm};
  background: ${theme.colors.surface};
  color: ${theme.colors.text};
  border: none;
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.primary}20;
    color: ${theme.colors.primary};
  }
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.text}60;
  padding: ${theme.spacing['2xl']};
`;

export const CandidateMessages: React.FC = () => {
  const [conversations, setConversations] = useState<Conversation[]>([
    {
      id: '1',
      subject: 'Candidature T3 - Documents manquants',
      lastMessage: 'Nous avons besoin de votre dernier justificatif de revenus',
      lastMessageTime: '2024-01-20T14:30:00',
      unreadCount: 2,
      status: 'open',
      relatedTo: 'Candidature #1'
    },
    {
      id: '2',
      subject: 'Demande d\'information logement T2',
      lastMessage: 'Votre dossier est complet, merci',
      lastMessageTime: '2024-01-19T10:15:00',
      unreadCount: 0,
      status: 'closed',
      relatedTo: 'Candidature #2'
    }
  ]);

  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      conversationId: '1',
      sender: 'organization',
      senderName: 'Agent validation',
      content: 'Bonjour, nous avons bien reçu votre candidature pour le logement T3.',
      timestamp: '2024-01-20T10:00:00',
      read: true
    },
    {
      id: '2',
      conversationId: '1',
      sender: 'organization',
      senderName: 'Agent validation',
      content: 'Nous avons besoin de votre dernier justificatif de revenus pour compléter votre dossier.',
      timestamp: '2024-01-20T14:30:00',
      read: false
    }
  ]);

  const [selectedConversation, setSelectedConversation] = useState<string>('1');
  const [newMessage, setNewMessage] = useState('');

  const getCurrentMessages = () => {
    return messages.filter(m => m.conversationId === selectedConversation);
  };

  const getCurrentConversation = () => {
    return conversations.find(c => c.id === selectedConversation);
  };

  const handleSendMessage = () => {
    if (!newMessage.trim()) return;

    const message: Message = {
      id: Date.now().toString(),
      conversationId: selectedConversation,
      sender: 'candidate',
      senderName: 'Vous',
      content: newMessage,
      timestamp: new Date().toISOString(),
      read: true
    };

    setMessages([...messages, message]);
    setNewMessage('');
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Hier';
    } else if (diffDays < 7) {
      return date.toLocaleDateString('fr-FR', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    }
  };

  return (
    <DashboardLayout>
      <PageHeader>
        <PageTitle>Messages</PageTitle>
        <PageSubtitle>
          Communiquez avec l'organisation concernant vos candidatures
        </PageSubtitle>
      </PageHeader>

      <FloatingChatbot />

      <MessagesContainer>
        <ConversationsList>
          <ConversationsHeader>
            <ConversationsTitle>Conversations</ConversationsTitle>
          </ConversationsHeader>
          <ConversationsScroll>
            {conversations.map(conv => (
              <ConversationItem
                key={conv.id}
                active={conv.id === selectedConversation}
                unread={conv.unreadCount > 0}
                onClick={() => setSelectedConversation(conv.id)}
              >
                <ConvSubject>
                  {conv.subject}
                  {conv.unreadCount > 0 && (
                    <UnreadBadge>{conv.unreadCount}</UnreadBadge>
                  )}
                </ConvSubject>
                <ConvPreview>{conv.lastMessage}</ConvPreview>
                <ConvTime>
                  {formatTime(conv.lastMessageTime)}
                  {conv.relatedTo && ` • ${conv.relatedTo}`}
                </ConvTime>
              </ConversationItem>
            ))}
          </ConversationsScroll>
        </ConversationsList>

        <ChatArea>
          {selectedConversation ? (
            <>
              <ChatHeader>
                <ChatTitle>{getCurrentConversation()?.subject}</ChatTitle>
                <ChatSubtitle>{getCurrentConversation()?.relatedTo}</ChatSubtitle>
              </ChatHeader>

              <MessagesArea>
                {getCurrentMessages().map(message => (
                  <MessageBubble key={message.id} $isOwn={message.sender === 'candidate'}>
                    <MessageHeader $isOwn={message.sender === 'candidate'}>
                      <FiUser />
                      <span>{message.senderName}</span>
                    </MessageHeader>
                    <MessageContent $isOwn={message.sender === 'candidate'}>
                      {message.content}
                    </MessageContent>
                    <MessageTime>{formatTime(message.timestamp)}</MessageTime>
                  </MessageBubble>
                ))}
              </MessagesArea>

              <InputArea>
                <InputContainer>
                  <AttachButton title="Joindre un fichier">
                    <FiPaperclip size={20} />
                  </AttachButton>
                  <TextInput
                    placeholder="Écrivez votre message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                  />
                  <SendButton
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim()}
                  >
                    <FiSend /> Envoyer
                  </SendButton>
                </InputContainer>
              </InputArea>
            </>
          ) : (
            <EmptyState>
              <FiMessageCircle size={64} style={{ marginBottom: theme.spacing.lg }} />
              <h3>Sélectionnez une conversation</h3>
              <p>Choisissez une conversation dans la liste pour commencer</p>
            </EmptyState>
          )}
        </ChatArea>
      </MessagesContainer>
    </DashboardLayout>
  );
};
